import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { billingApi } from '../lib/api';
import {
  CheckCircle, WarningCircle, Spinner, ArrowRight, Sparkle, ReceiptX,
} from '@phosphor-icons/react';

// Polling per Stripe playbook — up to 5 attempts, 2s apart
const MAX_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | success | expired | error
  const [detail, setDetail] = useState(null);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState('error');
      setDetail({ message: 'Missing session id.' });
      return;
    }
    try {
      const { data } = await billingApi.status(sessionId);
      if (data.payment_status === 'paid' && data.status === 'complete') {
        setState('success');
        setDetail(data);
        return;
      }
      if (data.status === 'expired') {
        setState('expired');
        setDetail(data);
        return;
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState('error');
        setDetail({ message: 'We could not confirm your payment in time. Please check your email for confirmation or contact support.' });
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err) {
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState('error');
        setDetail({ message: err.response?.data?.detail || 'Could not verify payment.' });
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    }
  }, [sessionId]);

  useEffect(() => {
    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [poll]);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="billing-success-page">
      <header className="w-full bg-[#f5f6fa] dark:bg-muted/30 border-b border-border">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/login" className="flex items-center gap-3">
            <div
              className="h-14 w-16 flex items-center justify-center text-white font-heading font-bold text-xl tracking-tight"
              style={{ background: '#002677' }}
            >
              BIL
            </div>
          </Link>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
            Billing
          </span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16"
        style={state === 'success' ? {
          background: 'linear-gradient(135deg, #eef8f0 0%, #e6f7ec 50%, #d9f0e1 100%)',
        } : undefined}
      >
        <div className="w-full max-w-xl bg-card border border-border rounded-sm shadow-2xl p-8 sm:p-10">
          {state === 'polling' && <Polling />}
          {state === 'success' && <Success detail={detail} onContinue={() => navigate('/register')} />}
          {state === 'expired' && <Expired onRetry={() => navigate('/pricing')} />}
          {state === 'error' && <ErrorState message={detail?.message} onRetry={() => navigate('/pricing')} />}
        </div>
      </main>
    </div>
  );
}

function Polling() {
  return (
    <div className="text-center space-y-4" data-testid="billing-polling">
      <div className="h-14 w-14 rounded-full bg-[#002677]/10 flex items-center justify-center mx-auto">
        <Spinner size={28} weight="bold" className="text-[#002677] animate-spin" />
      </div>
      <h1 className="font-heading text-2xl font-semibold tracking-tight">Confirming your payment…</h1>
      <p className="text-sm text-muted-foreground">
        We&rsquo;re talking to Stripe to finalize your plan. This usually takes a few seconds.
      </p>
    </div>
  );
}

function Success({ detail, onContinue }) {
  const plan = detail?.plan || 'Professional';
  const billing = detail?.billing || 'monthly';
  const amount = detail?.amount_total ? (detail.amount_total / 100).toFixed(2) : null;
  const trialEnds = detail?.trial_ends_at ? new Date(detail.trial_ends_at) : null;
  return (
    <div className="space-y-6" data-testid="billing-success">
      <div className="flex flex-col items-center text-center">
        <div className="h-16 w-16 rounded-full bg-[#00B140]/15 flex items-center justify-center mb-4">
          <CheckCircle size={32} weight="fill" className="text-[#00B140]" />
        </div>
        <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#00B140]">
          Payment received
        </p>
        <h1 className="font-heading text-3xl font-semibold tracking-tight mt-2">
          Welcome to BIL <span className="capitalize">{plan}</span>.
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Your 30-day access is active. You can cancel any time for a full refund within 30 days.
        </p>
      </div>

      <div className="bg-accent/30 rounded-sm border border-border p-4 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Plan</span>
          <span className="font-medium capitalize">{plan} · {billing}</span>
        </div>
        {amount && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Charged today</span>
            <span className="font-medium">${amount}</span>
          </div>
        )}
        {trialEnds && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Access until</span>
            <span className="font-medium">
              {trialEnds.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        )}
      </div>

      <Button
        onClick={onContinue}
        className="w-full rounded-sm h-12 font-medium"
        style={{ background: '#002677' }}
        data-testid="billing-continue-cta"
      >
        <Sparkle size={16} className="mr-2" weight="fill" />
        Create your account &amp; get started
        <ArrowRight size={16} className="ml-2" weight="bold" />
      </Button>
      <p className="text-[11px] text-muted-foreground text-center">
        A receipt has been emailed to you. Have questions? <a href="mailto:hello@ubil.net" className="underline">hello@ubil.net</a>.
      </p>
    </div>
  );
}

function Expired({ onRetry }) {
  return (
    <div className="text-center space-y-5" data-testid="billing-expired">
      <div className="h-14 w-14 rounded-full bg-[#FFD100]/25 flex items-center justify-center mx-auto">
        <ReceiptX size={28} weight="duotone" className="text-[#B5940A]" />
      </div>
      <h1 className="font-heading text-2xl font-semibold tracking-tight">
        This checkout session expired.
      </h1>
      <p className="text-sm text-muted-foreground">
        No worries — your card wasn&rsquo;t charged. Head back to the pricing page and pick your plan again.
      </p>
      <Button onClick={onRetry} className="w-full rounded-sm h-11" style={{ background: '#002677' }}>
        Back to pricing <ArrowRight size={16} className="ml-2" weight="bold" />
      </Button>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="text-center space-y-5" data-testid="billing-error">
      <div className="h-14 w-14 rounded-full bg-[#E4002B]/15 flex items-center justify-center mx-auto">
        <WarningCircle size={28} weight="duotone" className="text-[#E4002B]" />
      </div>
      <h1 className="font-heading text-2xl font-semibold tracking-tight">
        We couldn&rsquo;t confirm your payment.
      </h1>
      <p className="text-sm text-muted-foreground">{message || 'Please try again in a moment.'}</p>
      <Button onClick={onRetry} variant="outline" className="w-full rounded-sm h-11">
        Back to pricing
      </Button>
    </div>
  );
}
